import GLOBAL from "../../../../global";
import CONSTANT from "../../../../constants/Constant";
import * as tf from "@tensorflow/tfjs";
import * as poseDetection from "@tensorflow-models/pose-detection";

const CONFIDENCE_SCORE = 0.4;

let poseDetector = null;

const MOVENET_CONFIG = {
    maxPoses: 6,
    type: "lightning",
    scoreThreshold: 0.3,
};

const THRESHOLD_FOR_SHOULDERS_DISTANCE = 40;

export const NET_MODEL = MOVENET_CONFIG;
export const DEFAULT_LINE_WIDTH = 3;
export const DEFAULT_RADIUS = 3;
export const FULL_SCREEN_SKELETON_COLOR = "#5578EB";
export const MIN_SCREEN_SKELETON_COLOR = "#FFFFFF";
export const SPEECH_PROBABILITY_THRESHOLD = 0.9;

export const GUI_STATE = {
    detector: null,
    model: null,
    modelType: null,
    rafId: null,
    ctxFullScreen: null,
    ctxMinScreen: null,
};

export const KEYPOINTS = Object.freeze({
    NOSE: { name: "nose", value: "nose", point: 0 },
    LEFTEYEINNER: { name: "leftEyeInner", value: "left_eye_inner", point: 1 },
    LEFTEYE: { name: "leftEye", value: "left_eye", point: 2 },
    LEFTEYEOUTER: { name: "leftEyeOuter", value: "left_eye_outer", point: 3 },
    RIGHTEYEINNER: {
        name: "rightEyeInner",
        value: "right_eye_inner",
        point: 4,
    },
    RIGHTEYE: { name: "rightEye", value: "right_eye", point: 5 },
    RIGHTEYEOUTER: {
        name: "rightEyeOuter",
        value: "right_eye_outer",
        point: 6,
    },
    LEFTEAR: { name: "leftEar", value: "left_ear", point: 7 },
    RIGHTEAR: { name: "rightEar", value: "right_ear", point: 8 },
    MOUTHLEFT: { name: "mouthLeft", value: "mouth_left", point: 9 },
    MOUTHRIGHT: { name: "mouthRight", value: "mouth_right", point: 10 },
    LEFTSHOULDER: { name: "leftShoulder", value: "left_shoulder", point: 11 },
    RIGHTSHOULDER: {
        name: "rightShoulder",
        value: "right_shoulder",
        point: 12,
    },
    LEFTELBOW: { name: "leftElbow", value: "left_elbow", point: 13 },
    RIGHTELBOW: { name: "rightElbow", value: "right_elbow", point: 14 },
    LEFTWRIST: { name: "leftWrist", value: "left_wrist", point: 15 },
    RIGHTWRIST: { name: "rightWrist", value: "right_wrist", point: 16 },
    LEFTPINKY: { name: "leftPinky", value: "left_pinky", point: 17 },
    RIGHTPINKY: { name: "rightPinky", value: "right_pinky", point: 18 },
    LEFTINDEX: { name: "leftIndex", value: "left_index", point: 19 },
    RIGHTINDEX: { name: "rightIndex", value: "right_index", point: 20 },
    LEFTTHUMB: { name: "leftThumb", value: "left_thumb", point: 21 },
    RIGHTTHUMB: { name: "rightThumb", value: "right_thumb", point: 22 },
    LEFTHIP: { name: "leftHip", value: "left_hip", point: 23 },
    RIGHTHIP: { name: "rightHip", value: "right_hip", point: 24 },
    LEFTKNEE: { name: "leftKnee", value: "left_knee", point: 25 },
    RIGHTKNEE: { name: "rightKnee", value: "right_knee", point: 26 },
    LEFTANKLE: { name: "leftAnkle", value: "left_ankle", point: 27 },
    RIGHTANKLE: { name: "rightAnkle", value: "right_ankle", point: 28 },
    LEFTHEEL: { name: "leftHeel", value: "left_heel", point: 29 },
    RIGHTHEEL: { name: "rightHeel", value: "right_heel", point: 30 },
    LEFTFOOTINDEX: {
        name: "leftFootIndex",
        value: "left_foot_index",
        point: 31,
    },
    RIGHTFOOTINDEX: {
        name: "rightFootIndex",
        value: "right_foot_index",
        point: 32,
    },
});

export const LEFT_FACING_EXERCISE_CODE_NAMES = [
    "postureAnalysis",
    "lookCeiling",
    "chinToChest",
    "shoulderFlexionLeft",
    "shoulderExtensionLeft",
    "shoulderInternalRotationLeft",
    "shoulderExternalRotationLeft",
    "backFlexion",
    "headToTopBend",
    "kneeSquat",
    "hipExtensionLeft",
];

export const RIGHT_FACING_EXERCISE_CODE_NAMES = [
    "shoulderFlexionRight",
    "shoulderExtensionRight",
    "shoulderInternalRotationRight",
    "shoulderExternalRotationRight",
    "hipExtensionRight",
];

// ---------------------------------------------------------------------------
// Precomputed lookup: keypoint value string → array index  (O(1) access)
// ---------------------------------------------------------------------------
const VALUE_TO_POINT = Object.fromEntries(
    Object.values(KEYPOINTS).map(({ value, point }) => [value, point]),
);

const MIDPOINT_MAP = {
    mid_hip: ["left_hip", "right_hip"],
    mid_shoulder: ["left_shoulder", "right_shoulder"],
};

const KNOWN_VALUES = new Set([
    ...Object.values(KEYPOINTS).map((kp) => kp.value),
    ...Object.keys(MIDPOINT_MAP),
]);

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** O(1) index lookup – replaces the original O(n) Array.find scan. */
const getIndexByName = (name) => VALUE_TO_POINT[name];

const getMidpoint = (landmarks, leftValue, rightValue) => {
    const left = landmarks[VALUE_TO_POINT[leftValue]];
    const right = landmarks[VALUE_TO_POINT[rightValue]];
    if (!left || !right) return null;
    return { x: (left.x + right.x) / 2, y: (left.y + right.y) / 2 };
};

const resolvePoint = (landmarks, value) => {
    const midpointKeys = MIDPOINT_MAP[value];
    if (midpointKeys) return getMidpoint(landmarks, ...midpointKeys);
    return landmarks[VALUE_TO_POINT[value]] ?? null;
};

/**
 * Resolves a joint name to a landmark coordinate.
 * Handles the "mid_shoulder" virtual keypoint without allocating a
 * temporary object – passes the full landmarks array directly.
 */
const resolveJoint = (landmarks, jointName) => {
    if (jointName === "mid_shoulder") return getShoulderMidPoint(landmarks);
    const index = getIndexByName(jointName);
    return index !== undefined ? landmarks[index] : null;
};

const getAngle = (a, b, c) => {
    const abX = a.x - b.x,
        abY = a.y - b.y;
    const cbX = c.x - b.x,
        cbY = c.y - b.y;
    const dot = abX * cbX + abY * cbY;
    const magAB = Math.hypot(abX, abY);
    const magCB = Math.hypot(cbX, cbY);
    return (Math.acos(dot / (magAB * magCB)) * 180) / Math.PI;
};

const isRightHeelLeftOfRightAnkle = (keyPoints) =>
    keyPoints[KEYPOINTS.RIGHTHEEL.point]?.x <
    keyPoints[KEYPOINTS.RIGHTANKLE.point]?.x;

const isRightHeelLeftOfLeftAnkle = (keyPoints) =>
    keyPoints[KEYPOINTS.RIGHTHEEL.point]?.x <
    keyPoints[KEYPOINTS.LEFTANKLE.point]?.x;

const isShoulderToShoulderDistanceValid = (keyPoints) =>
    Math.abs(
        keyPoints[KEYPOINTS.LEFTSHOULDER.point]?.x -
            keyPoints[KEYPOINTS.RIGHTSHOULDER.point]?.x,
    ) < THRESHOLD_FOR_SHOULDERS_DISTANCE;

// ---------------------------------------------------------------------------
// Exported utilities
// ---------------------------------------------------------------------------

/** Returns false if any keypoint falls outside the global bounding box. */
export const checkFullBodyInBox = (keyPoints) =>
    !keyPoints.some(
        ({ x, y }) =>
            x <= GLOBAL.left ||
            x >= GLOBAL.right ||
            y <= GLOBAL.top ||
            y >= GLOBAL.bottom,
    );

export const getShoulderMidPoint = (keyPoints) => {
    const shoulderLeft = keyPoints[KEYPOINTS.LEFTSHOULDER.point];
    const shoulderRight = keyPoints[KEYPOINTS.RIGHTSHOULDER.point];
    if (!shoulderLeft || !shoulderRight) return null;

    return {
        score: Math.min(shoulderLeft.visibility, shoulderRight.visibility),
        x: shoulderLeft.x + (shoulderRight.x - shoulderLeft.x) / 2,
        y: shoulderLeft.y + (shoulderRight.y - shoulderLeft.y) / 2,
    };
};

export const isUserFacingRight = (keyPoints) => {
    if (
        !checkNoseInLeftSideOfShoulderMidPoint(keyPoints) &&
        checkNoseInRightSideOfShoulderMidPoint(keyPoints) &&
        isShoulderToShoulderDistanceValid(keyPoints)
    )
        return true;

    return (
        isRightHeelLeftOfRightAnkle(keyPoints) &&
        isRightHeelLeftOfLeftAnkle(keyPoints)
    );
};

export const isUserFacingLeft = (keyPoints) => {
    if (
        !checkNoseInRightSideOfShoulderMidPoint(keyPoints) &&
        checkNoseInLeftSideOfShoulderMidPoint(keyPoints) &&
        isShoulderToShoulderDistanceValid(keyPoints)
    )
        return true;

    return (
        !isRightHeelLeftOfRightAnkle(keyPoints) &&
        !isRightHeelLeftOfLeftAnkle(keyPoints)
    );
};

export const checkNoseInRightSideOfShoulderMidPoint = (keyPoints) => {
    const nosePoint = keyPoints[KEYPOINTS.NOSE.point];
    const shoulderMidPoint = getShoulderMidPoint(keyPoints);
    if (!nosePoint || !shoulderMidPoint) return false;
    return nosePoint.x >= shoulderMidPoint.x;
};

export const checkNoseInLeftSideOfShoulderMidPoint = (keyPoints) => {
    const nosePoint = keyPoints[KEYPOINTS.NOSE.point];
    const shoulderMidPoint = getShoulderMidPoint(keyPoints);
    if (!nosePoint || !shoulderMidPoint) return false;
    return nosePoint.x <= shoulderMidPoint.x;
};

/**
 * Determines the current rep phase by computing the joint angle for each
 * phase's restriction and returning the first matching PhaseDialogue.
 *
 * Input/output unchanged; optimizations:
 * - Removed dev-only console.log calls (perf + noise reduction)
 * - resolveJoint passes `landmarks` directly for mid_shoulder (no temp object)
 * - getIndexByName is now O(1)
 */
export const processPhase = (landmarks, keyPointRestriction) => {
    if (!landmarks || landmarks.length < 33) return CONSTANT.REP_PHASE.UNKNOWN;

    for (const phase of keyPointRestriction) {
        const r = phase.Restrictions[0];
        const a = resolveJoint(landmarks, r.StartKeyPosition);
        const b = resolveJoint(landmarks, r.MiddleKeyPosition);
        const c = resolveJoint(landmarks, r.EndKeyPosition);

        if (!a || !b || !c) continue;

        const angle = getAngle(a, b, c);

        // console.log("detected angle", angle, "for phase", phase.PhaseDialogue);

        if (angle >= r.MinValidationValue && angle <= r.MaxValidationValue) {
            return phase.PhaseDialogue;
        }
    }

    return CONSTANT.REP_PHASE.UNKNOWN;
};

/** Returns true when two numeric ranges [min1,max1] and [min2,max2] overlap. */
const rangesOverlap = (min1, max1, min2, max2) => max1 >= min2 && max2 >= min1;

export const getEquivalentStartPhases = (keyPointRestriction) => {
    const phase1 = keyPointRestriction.find((p) => p.PhaseNumber === 1);
    if (!phase1) return [];

    const { MinValidationValue: refMin, MaxValidationValue: refMax } =
        phase1.Restrictions[0];

    return keyPointRestriction
        .filter(({ Restrictions: [r] }) =>
            rangesOverlap(
                refMin,
                refMax,
                r.MinValidationValue,
                r.MaxValidationValue,
            ),
        )
        .map((p) => p.PhaseDialogue);
};

// ---------------------------------------------------------------------------
// Orientation detection
// ---------------------------------------------------------------------------

const ORIENTATION_KEY_TEXT = {
    left: "leftFacing",
    right: "rightFacing",
    front: "forwardFacing",
    back: "backwardFacing",
    good: "backwardFacing",
};

const detect_orientation = (nose, leftEar, rightEar) => {
    const leftSide = Math.sign(leftEar.x - nose.x);
    const rightSide = Math.sign(rightEar.x - nose.x);

    if (leftSide > 0 && rightSide > 0) return "left";
    if (leftSide < 0 && rightSide < 0) return "right";
    if (leftSide > 0 && rightSide < 0) return "front";
    if (leftSide < 0 && rightSide > 0) return "back";
    return "good";
};

export const person_orientation_check = (keyPoints, definedOrientation) => {
    const nose = keyPoints[KEYPOINTS.NOSE.point];
    const leftEar = keyPoints[KEYPOINTS.LEFTEAR.point];
    const rightEar = keyPoints[KEYPOINTS.RIGHTEAR.point];

    if ([nose, leftEar, rightEar].some((kp) => !kp || kp.visibility < 0.5)) {
        console.warn("Some keypoints are missing or not visible enough.");
        return false;
    }

    const target = definedOrientation.toLowerCase();

    // For lateral orientations, delegate to the shoulder/heel-based detectors
    // which are more robust than the ear-only heuristic.
    // if (target === "left"){
    //     console.log("left checking started.....")
    //     return isUserFacingLeft(keyPoints) || ORIENTATION_KEY_TEXT.left;
    // }
    // else if (target === "right"){
    //     console.log("right checking started.....")
    //     return isUserFacingRight(keyPoints) || ORIENTATION_KEY_TEXT.right;
    // }
    // else if (target === "front"){
    //     console.log("front checking started.....")
    //     return isUserFacingFront(keyPoints) || ORIENTATION_KEY_TEXT.front;
    // }

    // For front/back the ear-relative positions are the appropriate signal.
    const orientation = detect_orientation(nose, leftEar, rightEar);
    if (orientation === target) return true;
    return ORIENTATION_KEY_TEXT[target] ?? false;
};

// ---------------------------------------------------------------------------
// Calibration monitoring
// ---------------------------------------------------------------------------

const CALIBRATION_KEYPOINTS = [
    KEYPOINTS.NOSE,
    KEYPOINTS.LEFTEAR,
    KEYPOINTS.RIGHTEAR,
    KEYPOINTS.LEFTSHOULDER,
    KEYPOINTS.RIGHTSHOULDER,
    KEYPOINTS.LEFTELBOW,
    KEYPOINTS.RIGHTELBOW,
    KEYPOINTS.LEFTWRIST,
    KEYPOINTS.RIGHTWRIST,
    KEYPOINTS.LEFTHIP,
    KEYPOINTS.RIGHTHIP,
];

/**
 * Determines if the person is standing still.
 *
 * Bug fixed: the original used `{ point, key }` destructuring where `key`
 * does not exist on KEYPOINTS entries, collapsing `pts` to a single entry
 * (all `undefined` keys overwrite each other) and causing a TypeError when
 * the downstream destructuring tried to read `.x` / `.y` from `undefined`.
 * Now directly indexes into `landmarks` by the known point indices.
 *
 * Returns: "missingKeypoints" | "notFacingForward" | "handsProbablyCrossed"
 *        | "tryingToRaiseHands" | "good"
 */
export const calibration_monitoring = (landmarks) => {
    // Guard: every calibration keypoint must be present
    if (CALIBRATION_KEYPOINTS.some(({ point }) => landmarks[point] == null)) {
        console.warn("Some keypoints are missing.");
        return "missingKeypoints";
    }

    // Direct index reads – no intermediate object needed
    const leftWrist = landmarks[KEYPOINTS.LEFTWRIST.point];
    const rightWrist = landmarks[KEYPOINTS.RIGHTWRIST.point];
    const leftHip = landmarks[KEYPOINTS.LEFTHIP.point];
    const rightHip = landmarks[KEYPOINTS.RIGHTHIP.point];
    const leftElbow = landmarks[KEYPOINTS.LEFTELBOW.point];
    const rightElbow = landmarks[KEYPOINTS.RIGHTELBOW.point];

    if (!person_orientation_check(landmarks, "front")) {
        console.warn(
            "Please turn until you are facing forward with respect to the camera.",
        );
        return "notFacingForward";
    }

    // Hands not crossed; wrists within hip bounds
    const handsValid =
        leftWrist.x > rightWrist.x &&
        leftWrist.x > leftHip.x &&
        rightWrist.x < rightHip.x;

    if (!handsValid) {
        console.warn(
            "The hands are probably crossed. The person should keep their hands still.",
        );
        return "handsProbablyCrossed";
    }

    if (leftWrist.y < leftElbow.y || rightWrist.y < rightElbow.y) {
        console.warn(
            "The person is probably trying to raise their hands. They should keep their hands still.",
        );
        return "tryingToRaiseHands";
    }

    return "good";
};

// ---------------------------------------------------------------------------
// Lighting analysis
// ---------------------------------------------------------------------------

/**
 * Analyze lighting from a canvas ImageData object.
 *
 * Optimizations:
 * - GLOBAL visibility-box coordinates destructured once before the loop
 * - rowStride and colOffset computed once outside the loop
 * - Early return when pixelCount === 0 (avoids division-by-zero)
 */
export const lightingAnalyzer = (imageData) => {
    const { data, width } = imageData;
    const { x: left, y: top } = GLOBAL.top_left_visibility_box;
    const { x: right } = GLOBAL.top_right_visibility_box;
    const { y: bottom } = GLOBAL.bottom_left_visibility_box;

    const regionWidth = right - left;
    const pixelCount = regionWidth * (bottom - top);

    if (pixelCount === 0) return { brightness: 0, status: "No Data" };

    const rowStride = width * 4;
    const colOffset = left * 4;

    let brightnessSum = 0;
    let below30 = 0;
    let above220 = 0;

    for (let y = top; y < bottom; y++) {
        let idx = y * rowStride + colOffset;
        for (let x = 0; x < regionWidth; x++, idx += 4) {
            const brightness =
                0.299 * data[idx] +
                0.587 * data[idx + 1] +
                0.114 * data[idx + 2];
            brightnessSum += brightness;
            if (brightness < 30) below30++;
            if (brightness > 220) above220++;
        }
    }

    const normBrightness = brightnessSum / pixelCount / 255;
    const below30Percent = (below30 / pixelCount) * 100;
    const above220Percent = (above220 / pixelCount) * 100;

    let status = "poor";
    if (normBrightness >= 0.04 && normBrightness <= 0.8) {
        status = "good";
    } else if (normBrightness < 0.02 || below30Percent > 70) {
        status = "underexposed";
    } else if (normBrightness <= 0.04 || below30Percent > 50) {
        status = "poor";
    } else if (
        normBrightness <= 0.06 &&
        below30Percent < 30 &&
        above220Percent < 20
    ) {
        status = "proper";
    } else if (normBrightness > 0.08 || above220Percent > 70) {
        status = "overexposed";
    }

    return {
        brightness: Math.round(normBrightness * 1000) / 1000,
        status,
    };
};

// ---------------------------------------------------------------------------
// Person visibility check
// ---------------------------------------------------------------------------

/**
 * Determine if the required keypoints are within the visibility box.
 *
 * Optimization: bounds are destructured once; `.every()` short-circuits on
 * the first out-of-bounds keypoint.
 */
export const checkPersonVisibility = (landmarks, data) => {
    if (!Array.isArray(landmarks) || !Array.isArray(data) || data.length === 0)
        return false;

    const restriction = data[0]?.Restrictions?.[0];
    if (!restriction) return false;

    const minX = GLOBAL.top_left_visibility_box.x;
    const maxX = GLOBAL.top_right_visibility_box.x;
    const minY = GLOBAL.top_left_visibility_box.y;
    const maxY = GLOBAL.bottom_left_visibility_box.y;

    return Object.values(restriction)
        .filter((val) => typeof val === "string" && KNOWN_VALUES.has(val))
        .every((value) => {
            const point = resolvePoint(landmarks, value);
            return (
                point != null &&
                point.x >= minX &&
                point.x <= maxX &&
                point.y >= minY &&
                point.y <= maxY
            );
        });
};

// ---------------------------------------------------------------------------
// Multi-person detection (MoveNet)
// NOTE: Currently disabled in VideoPanel.jsx (isMultiPersonDetectionEnable = false)
// To re-enable, set isMultiPersonDetectionEnable = true in VideoPanel.jsx
// ---------------------------------------------------------------------------

export const loadMoveNetDetector = async () => {
    if (!poseDetector) {
        await tf.setBackend("webgl");
        await tf.ready();
        poseDetector = await poseDetection.createDetector(
            poseDetection.SupportedModels.MoveNet,
            { modelType: poseDetection.movenet.modelType.MULTIPOSE_LIGHTNING },
        );
    }
    return poseDetector;
};

export const detectMultiplePersons = async (imageData, detector) => {
    const inputTensor = tf.browser.fromPixels(imageData);
    const poses = await detector.estimatePoses(inputTensor, {
        maxPoses: 6,
        flipHorizontal: false,
    });
    inputTensor.dispose();

    const personCount = poses.length;
    return { personCount, hasMultiple: personCount > 1 };
};

// ---------------------------------------------------------------------------
// Posture side-view monitoring
// ---------------------------------------------------------------------------

const SIDE_VIEW_KEYPOINTS = [
    KEYPOINTS.LEFTSHOULDER,
    KEYPOINTS.LEFTELBOW,
    KEYPOINTS.LEFTWRIST,
    KEYPOINTS.LEFTHIP,
    KEYPOINTS.LEFTANKLE,
    KEYPOINTS.RIGHTANKLE,
    KEYPOINTS.RIGHTWRIST,
    KEYPOINTS.NOSE,
    KEYPOINTS.LEFTEAR,
];

export const posture_side_view_monitoring = (landmarks) => {
    if (SIDE_VIEW_KEYPOINTS.some(({ point }) => !landmarks[point])) {
        return "missingKeypoints";
    }

    const leftShoulder = landmarks[KEYPOINTS.LEFTSHOULDER.point];
    const leftElbow = landmarks[KEYPOINTS.LEFTELBOW.point];
    const leftWrist = landmarks[KEYPOINTS.LEFTWRIST.point];
    const leftHip = landmarks[KEYPOINTS.LEFTHIP.point];

    if (!isUserFacingLeft(landmarks)) return "notFacingLeft";

    const leftArmAngle = getAngle(leftShoulder, leftElbow, leftWrist);
    if (leftArmAngle < 160 || leftArmAngle > 180) return "armNotRelaxed";

    if (Math.abs(leftWrist.y - leftHip.y) >= 50) return "leftWristNotNearHip";
    if (Math.abs(leftShoulder.x - leftHip.x) >= 40) return "torsoNotUpright";

    return "good";
};
